// Ritme harian kota (ikut jam WIB di peta publik): aktivitas warga di sekitar gedung tertentu.
//  - Pasar     : pembeli berkeliling di depan lapak (05.30–10.00 & 15.30–18.00), tidak terlalu ramai.
//  - Lapangan  : anak-anak main bola sore (15.30–17.45) — bola bergulir, pemain mengejar.
//  - Masjid    : jamaah masuk menjelang adzan & keluar usai sholat (jadwal tetap WIB).
//  - Pabrik    : asap cerobong + pekerja di halaman saat jam kerja (08.00–16.00); buruh
//                bergerombol keluar gerbang 16.00–16.25 (jam pulang).
//  - Sekolah   : anak-anak berdatangan 06.15–06.50 (bus sekolah berhenti di depan 06.24–06.34),
//                bubar 13.00–13.25 (bus menjemput 12.59–13.11).
// Semua posisi dihitung stateless dari (jam, t, seed) → tidak perlu state simulasi.
// NPC digambar lewat lapisan mover engine (drawOccluded) agar urut kedalaman & teroklusi gedung depan.
import { hashN } from "./sky";
import { TILE_W } from "./iso";
import { refHeight, paintBuildingAt } from "./paint";
import { sudutTapak } from "./konstruksi";
import { blitNpc } from "./npcSprite";

export const WAKTU_SHOLAT = [
  { nama: "Subuh", jam: 4 + 20 / 60 }, { nama: "Dzuhur", jam: 11 + 40 / 60 }, { nama: "Ashar", jam: 15 },
  { nama: "Maghrib", jam: 17 + 40 / 60 }, { nama: "Isya", jam: 18 + 50 / 60 }
];
export const JAM = {
  pasar: [[5.5, 10], [15.5, 18]],
  bola: [[15.5, 17.75]],
  pabrikKerja: [[8, 16]],
  pabrikPulang: [16, 16 + 25 / 60],
  sekolahMasuk: [6.25, 6 + 50 / 60], busPagi: 6.4,          // anak datang 06.15–06.50; bus berhenti 06.24–06.34
  sekolahBubar: [13, 13 + 25 / 60], busSiang: 12 + 59 / 60   // bubar 13.00–13.25; bus berhenti 12.59–13.11
};
const MNT = 1 / 60;
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const ease = (x) => { const e = clamp01(x); return e * e * (3 - 2 * e); };
const lerp = (a, b, k) => [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
// Trotoar = 0.16 tile di luar tepi depan tapak (sinkron movers.js SIDE) → dalam satuan v tapak
const luar = (S) => 0.16 / S.h;

// Ramp 0..1 untuk jendela [a,b] dengan tepi lembut `tepi` jam (default ±6 menit)
export function jendela(h, a, b, tepi = 0.1) {
  if (h < a - tepi || h > b + tepi) return 0;
  if (h < a) return (h - (a - tepi)) / tepi;
  if (h > b) return ((b + tepi) - h) / tepi;
  return 1;
}
const ramp = (h, daftar) => Math.max(0, ...daftar.map(([a, b]) => jendela(h, a, b)));

// Pilih def karakter: utamakan bid pada daftar, fallback karakter apa pun
function pemilih(defs) {
  const kar = (defs || []).filter(d => d.category === "Karakter" && d.paintCode);
  const byId = Object.fromEntries(kar.map(d => [d.bid, d]));
  return (bids, seed) => {
    const ada = bids.map(b => byId[b]).filter(Boolean);
    const pool = ada.length ? ada : kar;
    return pool.length ? pool[Math.floor(hashN(seed) * pool.length) % pool.length] : null;
  };
}

// Jalur polyline di koordinat tapak (u,v); posisi & arah pada parameter s (0..1, merata per ruas)
function diJalur(pts, s) {
  const n = pts.length - 1, k = Math.min(n - 1, Math.floor(clamp01(s) * n)), f = clamp01(s) * n - k;
  return { p: lerp(pts[k], pts[k + 1], f), arah: [pts[k + 1][0] - pts[k][0], pts[k + 1][1] - pts[k][1]] };
}

export function createAktivitas(c) {
  // c: { gridToScreen, cam, drawOccluded, get atmos() }
  let pilih = pemilih([]);
  let defsRef = null, busDef = null;

  // Arah hadap sprite dari vektor gerak lokal tapak (du,dv) → arah dunia (sadar rotasi gedung)
  function arahDari(S, u, v, duv) {
    const [gx, gy] = S.gpt(u, v), [hx, hy] = S.gpt(u + duv[0], v + duv[1]);
    const wx = hx - gx, wy = hy - gy;
    return Math.abs(wx) + Math.abs(wy) < 1e-6 ? "SW" : Math.abs(wx) >= Math.abs(wy) ? (wx > 0 ? "SE" : "NW") : (wy > 0 ? "SW" : "NE");
  }

  // Item mover: NPC pada titik tapak (u,v) — arah dari vektor gerak lokal (du,dv) via gpt
  function npcItem(S, u, v, def, duv, tAnim, seed, alpha) {
    if (!def || alpha <= 0.02) return null;
    const [gx, gy] = S.gpt(u, v), dir = arahDari(S, u, v, duv);
    const zoom = c.cam.zoom, malam = c.atmos.dark;
    return { d: gx + gy, f: () => c.drawOccluded(gx, gy, 22 * zoom, (g, sx, sy) => blitNpc(g, def, sx, sy, zoom, malam, tAnim, seed, dir, alpha)) };
  }

  // Item mover: kendaraan katalog (mis. bus sekolah) pada titik tapak (u,v)
  function kendaraanItem(S, u, v, def, duv, t, alpha) {
    if (!def || alpha <= 0.02) return null;
    const [gx, gy] = S.gpt(u, v), dir = arahDari(S, u, v, duv);
    const zoom = c.cam.zoom, malam = c.atmos.dark;
    return { d: gx + gy, f: () => c.drawOccluded(gx, gy, 48 * zoom, (g, sx, sy) => {
      g.save(); if (alpha < 1) g.globalAlpha = alpha;
      paintBuildingAt(g, { ...def, category: "Kendaraan" }, sx, sy, zoom, { time: t, night: malam, dir });
      g.restore();
    }) };
  }

  // ---- PASAR: 2–3 pembeli berjalan antar lapak lalu berhenti sejenak (stateless wander)
  function pasar(S, b, h, t, seed, out) {
    const a = ramp(h, JAM.pasar);
    if (a <= 0) return;
    const n = S.w * S.h >= 16 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const sd = seed + i * 17.3, tA = t * 0.2 + hashN(sd) * 9, k = Math.floor(tA), f = tA - k;
      const wp = (kk) => [0.1 + 0.8 * hashN(sd + kk * 3.7), 0.8 + (0.2 + luar(S)) * hashN(sd + kk * 5.1)];
      const A = wp(k), B = wp(k + 1), e = ease(f / 0.62), p = lerp(A, B, e);
      const def = pilih(["npc_ibu_belanja", "npc_warga_sipil", "npc_lansia"], sd);
      const it = npcItem(S, p[0], p[1], def, [B[0] - A[0], B[1] - A[1]], (k + e) * 2.6, sd, a);
      if (it) out.push(it);
    }
  }

  // ---- LAPANGAN: bola menggelinding (lissajous) + 6 pemain mengejar dengan jeda reaksi
  function bola(S, b, h, t, seed, out, T) {
    const a = ramp(h, JAM.bola);
    if (a <= 0) return;
    const posBola = (tt) => [0.5 + 0.28 * Math.sin(tt * 0.55 + seed), 0.5 + 0.28 * Math.sin(tt * 0.41 + seed * 1.3 + 1.2)];
    const B = posBola(t), Bv = posBola(t - 0.15);
    const loncat = Math.abs(Math.sin(t * 2.6)) * 0.09 * T;
    // bola (item kedalaman sendiri)
    {
      const [gx, gy] = S.gpt(B[0], B[1]);
      const zoom = c.cam.zoom, r = Math.max(1.5, 0.045 * T);
      out.push({ d: gx + gy, f: () => c.drawOccluded(gx, gy, 10 * zoom, (g, sx, sy) => {
        g.save(); g.globalAlpha = a;
        g.fillStyle = "rgba(0,0,0,0.25)"; g.beginPath(); g.ellipse(sx, sy, r * 1.1, r * 0.5, 0, 0, 7); g.fill();
        g.fillStyle = "#f4f4f4"; g.beginPath(); g.arc(sx, sy - r - loncat, r, 0, 7); g.fill();
        g.strokeStyle = "#222"; g.lineWidth = Math.max(0.6, r * 0.25); g.beginPath(); g.arc(sx, sy - r - loncat, r * 0.45, 0, 7); g.stroke();
        g.restore();
      }) });
    }
    for (let i = 0; i < 6; i++) {
      const sd = seed + i * 11.7, lag = 0.4 + hashN(sd) * 0.9, ang = hashN(sd + 1) * 6.28 + t * 0.15 * (i % 2 ? 1 : -1);
      const jarak = 0.16 + 0.1 * Math.sin(t * 0.7 + i);
      const P = posBola(t - lag), p = [P[0] + Math.cos(ang) * jarak, P[1] + Math.sin(ang) * jarak * 0.9];
      const def = pilih(["npc_anak_sekolah", "npc_pelajar_sma", "npc_pelari_jogging"], sd);
      const it = npcItem(S, clamp01(p[0]), clamp01(p[1]), def, [B[0] - Bv[0] + 1e-4, B[1] - Bv[1]], t * 1.6 + i, sd, a);
      if (it) out.push(it);
    }
  }

  // ---- MASJID: jamaah masuk (adzan −8 … +3 mnt) & keluar (+12 … +22 mnt) lewat pintu depan (u=.5, v=1)
  function masjid(S, b, h, t, seed, out) {
    for (const ws of WAKTU_SHOLAT) {
      const dMasuk = (h - (ws.jam - 8 * MNT)) / (11 * MNT), dKeluar = (h - (ws.jam + 12 * MNT)) / (10 * MNT);
      const masuk = dMasuk > -0.05 && dMasuk < 1.3, keluar = dKeluar > -0.05 && dKeluar < 1.3;
      if (!masuk && !keluar) continue;
      const n = 8;
      for (let i = 0; i < n; i++) {
        const sd = seed + i * 13.1 + ws.jam * 7, u0 = 0.04 + 0.92 * hashN(sd), ofs = 0.3 * hashN(sd + 2);
        const tr = 1 + luar(S), jalur = [[u0, tr], [0.5, tr], [0.5, 1.0], [0.5, 0.88]];
        const sRaw = masuk ? (dMasuk * 1.3 - ofs) : (dKeluar * 1.3 - ofs);
        if (sRaw <= 0 || sRaw >= 1) continue;
        const s = masuk ? sRaw : 1 - sRaw;                                   // keluar = jalur dibalik
        const { p, arah } = diJalur(jalur, s);
        const alpha = Math.min(1, s > 0.8 ? (1 - s) / 0.2 : 1, sRaw < 0.08 ? sRaw / 0.08 : 1);
        const def = pilih(["npc_warga_sipil", "npc_lansia", "npc_anak_sekolah", "npc_pekerja_kantoran"], sd);
        const it = npcItem(S, p[0], p[1], def, masuk ? arah : [-arah[0], -arah[1]], t * 1.1 + i, sd, alpha);
        if (it) out.push(it);
      }
    }
  }

  // ---- SEKOLAH: pagi anak-anak berdatangan (06.15–06.50) sebagian diantar bus sekolah yang berhenti
  // di depan gerbang (06.24–06.34); siang bubar 13.00–13.25 — sebagian naik bus (13.00–13.12), sisanya jalan.
  // Bus: lajur kiri jalan depan (0.33 tile di luar tepi), datang 20 dtk → berhenti → pergi 20 dtk.
  function sekolah(S, b, h, t, seed, out) {
    const tr = 1 + luar(S), vBus = 1 + 0.46 / S.h, vPintuBus = 1 + 0.3 / S.h;
    const [m0, m1] = JAM.sekolahMasuk, [b0, b1] = JAM.sekolahBubar;
    // Bus pagi & siang: {mulai (jam), lamaBerhenti (dtk)}
    const posBus = (mulai, lama) => {
      const s = (h - mulai) * 3600;
      if (s < 0 || s > lama + 40) return null;
      if (s < 20) return { u: -0.7 + 1.2 * ease(s / 20), berhenti: false, fase: s / 20 };
      if (s < 20 + lama) return { u: 0.5, berhenti: true, sejak: s - 20 };
      return { u: 0.5 + 1.2 * ease((s - 20 - lama) / 20), berhenti: false, fase: (s - 20 - lama) / 20 };
    };
    const bus = posBus(JAM.busPagi, 600) || posBus(JAM.busSiang, 720);
    if (bus && busDef) {
      const it = kendaraanItem(S, bus.u, vBus, busDef, [1, 0], t, 1);
      if (it) out.push(it);
    }
    const anak = (sd) => pilih(["npc_anak_sekolah", "npc_pelajar_sma"], sd);
    // Anak jalan kaki masuk (pagi) / keluar (siang) lewat trotoar → gerbang (u=.5, v=1)
    const dMasuk = (h - m0) / (m1 - m0), dBubar = (h - b0) / (b1 - b0);
    const masuk = dMasuk > -0.05 && dMasuk < 1.4, bubar = dBubar > -0.05 && dBubar < 1.4;
    if (masuk || bubar) {
      for (let i = 0; i < 10; i++) {
        const sd = seed + i * 9.1, u0 = hashN(sd) < 0.5 ? -0.25 + 0.3 * hashN(sd + 1) : 0.95 + 0.3 * hashN(sd + 1), ofs = 0.4 * hashN(sd + 2);
        const sRaw = (masuk ? dMasuk : dBubar) * 1.4 - ofs;
        if (sRaw <= 0 || sRaw >= 1) continue;
        const jalur = [[u0, tr], [0.5, tr], [0.5, 1.0], [0.5, 0.9]], s = masuk ? sRaw : 1 - sRaw;
        const { p, arah } = diJalur(jalur, s);
        const alpha = Math.min(1, s > 0.8 ? (1 - s) / 0.2 : 1, sRaw < 0.08 ? sRaw / 0.08 : 1);
        const it = npcItem(S, p[0], p[1], anak(sd), masuk ? arah : [-arah[0], -arah[1]], t * 1.5 + i, sd, alpha);
        if (it) out.push(it);
      }
    }
    // Anak turun dari bus (pagi) ke gerbang / naik bus (siang) dari gerbang — saat bus berhenti
    if (bus?.berhenti) {
      const pagi = h < 10, durasi = pagi ? 150 : 300;
      for (let i = 0; i < 6; i++) {
        const sd = seed + 101 + i * 5.3, sRaw = (bus.sejak - i * (durasi / 6) * 0.6) / (durasi * 0.45);
        if (sRaw <= 0 || sRaw >= 1) continue;
        const jalur = [[0.5, vPintuBus], [0.5, 1.0], [0.5, 0.9]], s = pagi ? sRaw : 1 - sRaw;
        const { p, arah } = diJalur(jalur, s);
        const alpha = Math.min(1, s > 0.75 ? (1 - s) / 0.25 : 1, sRaw < 0.1 ? sRaw / 0.1 : 1);
        const it = npcItem(S, p[0], p[1], anak(sd), pagi ? arah : [-arah[0], -arah[1]], t * 1.5 + i, sd, alpha);
        if (it) out.push(it);
      }
    }
  }

  // ---- PABRIK: jam kerja → 2 pekerja mondar-mandir di halaman depan; 16.00 → gerombolan buruh keluar gerbang
  function pabrik(S, b, h, t, seed, out) {
    const kerja = ramp(h, JAM.pabrikKerja);
    if (kerja > 0) {
      for (let i = 0; i < 2; i++) {
        const sd = seed + i * 19.3, ph = ((t * 0.07 + hashN(sd)) % 2), u = 0.15 + 0.7 * (ph < 1 ? ph : 2 - ph);
        const def = pilih(["npc_pekerja_konstruksi", "npc_satpam"], sd);
        const it = npcItem(S, u, 0.9 + i * 0.06, def, [ph < 1 ? 1 : -1, 0], t * 1.2 + i, sd, kerja);
        if (it) out.push(it);
      }
    }
    const [p0, p1] = JAM.pabrikPulang, dP = (h - p0) / (p1 - p0);
    if (dP > -0.05 && dP < 1.3) {
      const n = S.w * S.h >= 25 ? 12 : 8;
      for (let i = 0; i < n; i++) {
        const sd = seed + i * 7.9, ofs = 0.45 * hashN(sd), sRaw = dP * 1.45 - ofs;
        if (sRaw <= 0 || sRaw >= 1) continue;
        const kanan = hashN(sd + 3) < 0.5, uAkhir = kanan ? 1.3 : -0.3;
        const tr = 1 + luar(S), jalur = [[0.5, 0.9], [0.5, tr], [kanan ? 0.75 : 0.25, tr], [uAkhir, tr]];
        const { p, arah } = diJalur(jalur, sRaw);
        const alpha = Math.min(1, sRaw < 0.1 ? sRaw / 0.1 : 1, sRaw > 0.85 ? (1 - sRaw) / 0.15 : 1);
        const def = pilih(["npc_pekerja_konstruksi", "npc_warga_sipil"], sd);
        const it = npcItem(S, p[0], p[1], def, arah, t * 1.3 + i, sd, alpha);
        if (it) out.push(it);
      }
    }
  }

  // Asap cerobong pabrik (dilukis setelah lapisan mover, di atas gedung) saat jam kerja
  function asapPabrik(ctx, S, def, h, t, seed, T) {
    const kerja = ramp(h, JAM.pabrikKerja);
    if (kerja <= 0 || c.cam.zoom < 0.3) return;
    const tinggi = refHeight(def.floors ?? 1, S.w, S.h, c.cam.zoom) + 0.25 * T;
    const [cx, cy] = S.pt(0.25, 0.25, tinggi);
    ctx.save();
    for (let k = 0; k < 6; k++) {
      const umur = ((t * 0.22 + k / 6 + hashN(seed + k) * 0.1) % 1);
      const r = T * (0.07 + umur * 0.2), x = cx + Math.sin(t * 0.4 + k) * T * 0.05 + umur * T * 0.3, y = cy - umur * T * 0.9;
      ctx.globalAlpha = kerja * (1 - umur * umur) * (c.atmos.dark > 0.5 ? 0.3 : 0.55);
      ctx.fillStyle = c.atmos.dark > 0.5 ? "#8e97a3" : "#cfd5db";
      ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
    }
    ctx.restore();
  }

  const JENIS = { pasar: "pasar", lapangan: "bola", masjid: "masjid", pabrik_kecil: "pabrik", pabrik_besar: "pabrik", sekolah: "sekolah" };
  const HANDLER = { pasar, bola, masjid, pabrik, sekolah };

  // Kumpulkan item mover (NPC/bola) untuk frame ini — digabung ke lapisan mover engine
  function items(state, defs, hour, t, W, H) {
    if (defs !== defsRef) { defsRef = defs; pilih = pemilih(defs); busDef = (defs || []).find(d => d.bid === "bus_sekolah" && d.paintCode) || null; }
    const out = [], byId = {};
    for (const d of defs || []) byId[d.bid] = d;
    const T = TILE_W * c.cam.zoom;
    for (const b of state.buildings || []) {
      const jenis = JENIS[b.bid];
      if (!jenis) continue;
      const def = byId[b.bid];
      if (!def) continue;
      const S = sudutTapak(b, def, c.gridToScreen);
      const xs = [S.top[0], S.right[0], S.bottom[0], S.left[0]], ys = [S.top[1], S.right[1], S.bottom[1], S.left[1]];
      if (Math.max(...xs) < -80 || Math.min(...xs) > W + 80 || Math.max(...ys) < -120 || Math.min(...ys) > H + 80) continue;
      HANDLER[jenis](S, b, hour, t, (b.x * 7 + b.y * 13) % 10 + 0.37, out, T);
    }
    return out;
  }

  // Efek di atas gedung (asap pabrik) — dipanggil setelah lapisan mover
  function efek(ctx, state, defs, hour, t, W, H) {
    const byId = {};
    for (const d of defs || []) byId[d.bid] = d;
    const T = TILE_W * c.cam.zoom;
    for (const b of state.buildings || []) {
      if (JENIS[b.bid] !== "pabrik" || !byId[b.bid]) continue;
      const S = sudutTapak(b, byId[b.bid], c.gridToScreen);
      if (S.top[0] < -120 || S.top[0] > W + 120 || S.top[1] < -200 || S.top[1] > H + 120) continue;
      asapPabrik(ctx, S, byId[b.bid], hour, t, (b.x * 7 + b.y * 13) % 10 + 0.37, T);
    }
  }

  return { items, efek };
}
